import { useState, useEffect, useRef } from "react";

/* Dependency-free charts (plain SVG/CSS) in the Rush Republic black-and-white style.
   Null values are "unavailable" and are never drawn as zero. */

const compact = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
const full = new Intl.NumberFormat("en-US");
export const fmtCompact = (v) => (v === null || v === undefined ? "N/A" : compact.format(v));
export const fmtFull = (v) => (v === null || v === undefined ? "N/A" : full.format(v));

export const SERIES_COLORS = ["#0e0e0e", "#8f8f8a"];
// Display order everywhere (lines, bars, legend, summary chips): Reel, Carousel, Post
export const TYPE_SERIES = [
  { key: "REEL", name: "Reel", color: "#e5383b" },
  { key: "CAROUSEL", name: "Carousel", color: "#1fac71" },
  { key: "POST", name: "Post", color: "#f5b700" },
];

/** Table | Graph segmented control (keyboard accessible radio group). */
export function ViewToggle({ value, onChange, label }) {
  const opts = [["table", "Table"], ["graph", "Graph"], ["bar", "Bar Chart"]];
  const onKey = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const i = opts.findIndex(([k]) => k === value);
    onChange(opts[(i + (e.key === "ArrowRight" ? 1 : opts.length - 1)) % opts.length][0]);
  };
  return (
    <div className="view-toggle" role="radiogroup" aria-label={label} onKeyDown={onKey}>
      {opts.map(([k, text]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} tabIndex={value === k ? 0 : -1}
          className={value === k ? "is-active" : ""} onClick={() => onChange(k)}>{text}</button>
      ))}
    </div>
  );
}

/** Metric chooser (one metric at a time, so different scales are never mixed on one axis). */
export function MetricPicker({ options, value, onChange, label }) {
  return (
    <div className="chart-picker" role="group" aria-label={label}>
      {options.map(([k, text]) => (
        <button key={k} type="button" className={value === k ? "is-active" : ""} aria-pressed={value === k} onClick={() => onChange(k)}>{text}</button>
      ))}
    </div>
  );
}

/** Horizontal bars: rows = [{ key, label, sub, value }], sorted as given. */
export function HBarChart({ rows, title, metricLabel, onRowClick }) {
  const max = Math.max(0, ...rows.map((r) => r.value ?? 0));
  const ticks = max > 0 ? [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f)) : [0];
  return (
    <figure className="chart" aria-label={title}>
      <figcaption className="chart-title">{title}</figcaption>
      <div className="hbar-scale" aria-hidden="true">
        <span />
        <div className="hbar-ticks">{ticks.map((t, i) => <span key={i}>{fmtCompact(t)}</span>)}</div>
        <span />
      </div>
      <div className="hbar-list">
        {rows.map((r) => {
          const pct = max > 0 && r.value !== null ? Math.max(1.5, (r.value / max) * 100) : 0;
          return (
            <div className="hbar-row" key={r.key} title={`${r.label} · ${r.sub}\n${metricLabel}: ${fmtFull(r.value)}`}
              onClick={onRowClick ? () => onRowClick(r) : undefined} role={onRowClick ? "button" : undefined} tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? (e) => { if (e.key === "Enter") onRowClick(r); } : undefined}>
              <div className="hbar-label"><span className="hbar-name">{r.label}</span><span className="hbar-sub">{r.sub}</span></div>
              <div className="hbar-track">{r.value !== null && <div className="hbar-fill" style={{ width: `${pct}%` }} />}</div>
              <div className="hbar-value">{r.value === null ? <span className="metrics-na">N/A</span> : fmtCompact(r.value)}</div>
            </div>
          );
        })}
      </div>
      <div className="chart-axis-label">{metricLabel} (lifetime) per post</div>
    </figure>
  );
}

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(640);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    setW(el.clientWidth || 640);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((e) => setW(Math.max(260, Math.round(e[0].contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect(); // no leaks when the view is switched away
  }, []);
  return [ref, w];
}

const niceMax = (v) => {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const f = v / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * p;
};

/**
 * Month chart. points = [{ label, values: [v | null, ...] }] in chronological order; series = [{ name, color }].
 * kind "line" (one series, gaps where null) or "bars" (grouped).
 */
export function MonthChart({ points, series, kind, title, yLabel }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const H = 280, m = { t: 14, r: 14, b: 46, l: 52 };
  const iw = Math.max(120, width - m.l - m.r), ih = H - m.t - m.b;
  const all = points.flatMap((p) => p.values).filter((v) => v !== null && v !== undefined);
  const top = niceMax(Math.max(0, ...all));
  const y = (v) => m.t + ih - (v / top) * ih;
  const n = points.length;
  const step = iw / Math.max(1, n);
  const cx = (i) => m.l + step * i + step / 2;
  const gridVals = [0, 0.25, 0.5, 0.75, 1].map((f) => top * f);
  const every = step < 46 ? Math.ceil(46 / step) : 1;

  // One path per series; a null value breaks the line (a gap), it is never drawn as zero.
  const linePaths = series.map((_, k) => {
    let d = "";
    let pen = false;
    points.forEach((p, i) => {
      const v = p.values[k];
      if (v === null || v === undefined) { pen = false; return; }
      d += `${pen ? "L" : "M"}${cx(i).toFixed(1)},${y(v).toFixed(1)} `;
      pen = true;
    });
    return d;
  });
  const barW = Math.min(34, (step * 0.7) / series.length);

  return (
    <figure className="chart" aria-label={title} ref={ref} style={{ position: "relative" }}>
      <figcaption className="chart-title">{title}</figcaption>
      {all.length === 0 ? <div className="chart-empty">No values available for these months yet.</div> : (
        <>
          <svg className="chart-svg" width={width} height={H} style={{ width, height: H }} role="img" aria-label={title} onMouseLeave={() => setHover(null)}>
            {gridVals.map((g, i) => (
              <g key={i}>
                <line x1={m.l} x2={m.l + iw} y1={y(g)} y2={y(g)} stroke="rgba(14,14,14,.12)" />
                <text x={m.l - 8} y={y(g) + 4} textAnchor="end" fontSize="11" fill="rgba(14,14,14,.6)">{fmtCompact(Math.round(g))}</text>
              </g>
            ))}
            <text transform={`translate(12 ${m.t + ih / 2}) rotate(-90)`} textAnchor="middle" fontSize="11" fill="rgba(14,14,14,.6)">{yLabel}</text>
            {kind === "line" && linePaths.map((d, k) => <path key={k} d={d} fill="none" stroke={series[k].color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />)}
            {points.map((p, i) => (
              <g key={i} onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} tabIndex={0}
                aria-label={`${p.label}: ${series.map((s, k) => `${s.name} ${fmtFull(p.values[k])}`).join(", ")}`}>
                <rect x={m.l + step * i} y={m.t} width={step} height={ih} fill={hover === i ? "rgba(14,14,14,.05)" : "transparent"} />
                {kind === "line"
                  ? series.map((s, k) => (p.values[k] !== null && p.values[k] !== undefined
                    ? <circle key={k} cx={cx(i)} cy={y(p.values[k])} r={hover === i ? 5.5 : 4} fill="#fff" stroke={s.color} strokeWidth="2.5" /> : null))
                  : series.map((s, k) => {
                    const v = p.values[k];
                    if (v === null || v === undefined) return null;
                    const x = cx(i) - (barW * series.length) / 2 + barW * k;
                    return <rect key={k} x={x} y={y(v)} width={barW - 2} height={Math.max(1, m.t + ih - y(v))} fill={s.color} />;
                  })}
                {i % every === 0 && <text x={cx(i)} y={H - 24} textAnchor="middle" fontSize="11" fill="rgba(14,14,14,.7)">{p.label}</text>}
              </g>
            ))}
            <line x1={m.l} x2={m.l + iw} y1={m.t + ih} y2={m.t + ih} stroke="rgba(14,14,14,.5)" />
          </svg>
          {hover !== null && (
            <div className="chart-tip" style={{ left: Math.min(width - 170, Math.max(0, cx(hover) - 80)), top: 40 }}>
              <strong>{points[hover].label}</strong>
              {series.map((s, k) => (
                <div key={s.name}><i style={{ background: s.color }} />{s.name}: {fmtFull(points[hover].values[k])}</div>
              ))}
            </div>
          )}
          <div className="chart-legend">
            {series.map((s) => <span key={s.name}><i style={{ background: s.color }} />{s.name}</span>)}
          </div>
        </>
      )}
    </figure>
  );
}

const round1 = (v) => Math.round(v * 10) / 10;

/**
 * Reel vs Post vs Carousel over time as a multi-line chart.
 * buckets = [{ key, label }], cells[type][i] = { items, total, n } for the chosen metric.
 * "Average per post" is the fair way to compare types with different post counts; "Total" is also offered.
 */
export function TypeTrendChart({ buckets, cells, metricLabel, measure = "avg", title, kind = "line" }) {
  const value = (c) => {
    if (!c || c.items === 0 || c.total === null || c.total === undefined || !c.n) return null;
    return measure === "avg" ? round1(c.total / c.n) : c.total;
  };
  const points = buckets.map((b, i) => ({ label: b.label, values: TYPE_SERIES.map((t) => value(cells[t.key]?.[i])) }));
  // Overall comparison across the whole period (always weighted by post count).
  const overall = TYPE_SERIES.map((t) => {
    const list = cells[t.key] || [];
    const total = list.reduce((a, c) => a + (c.total || 0), 0);
    const n = list.reduce((a, c) => a + (c.n || 0), 0);
    const posts = list.reduce((a, c) => a + (c.items || 0), 0);
    return { ...t, avg: n ? total / n : null, total: n ? total : null, posts };
  });
  const ranked = overall.filter((o) => o.avg !== null).sort((a, b) => (measure === "avg" ? b.avg - a.avg : b.total - a.total));
  const best = ranked[0];
  return (
    <div>
      <MonthChart kind={kind} title={title} yLabel={measure === "avg" ? `Avg ${metricLabel.toLowerCase()} per post` : metricLabel}
        series={TYPE_SERIES.map((t) => ({ name: t.name, color: t.color }))} points={points} />
      <div className="type-compare">
        {best ? (
          <div className="type-best"><strong>{best.name}</strong> is performing best: {fmtFull(round1(best.avg))} {metricLabel.toLowerCase()} per post on average ({best.posts} {best.posts === 1 ? "post" : "posts"}).</div>
        ) : <div className="type-best">No values available yet to compare content types.</div>}
        <div className="type-chips">
          {overall.map((o) => (
            <span key={o.key} className={best && best.key === o.key ? "is-best" : ""}>
              <i style={{ background: o.color }} />{o.name}: {o.avg === null ? "N/A" : fmtFull(round1(o.avg))} avg · {o.posts} {o.posts === 1 ? "post" : "posts"}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
